import type { TutorSkill } from '../models/Tutor';
import { TUTOR_SKILLS } from './tutorSkillOptions';
import {
  SEARCH_TUTORS_PATH,
  TUTOR_SEARCH_OFFLINE_PATH,
  TUTOR_SEARCH_ONLINE_PATH,
} from './searchPaths';

/**
 * Public tutor discovery is split into two backend endpoints:
 * - `GET /v1/public/tutors/search/online`
 * - `GET /v1/public/tutors/search/offline?city=...&sessionType=AT_TEACHER|AT_STUDENT`
 *
 * Routes use `/user/tutors/online` and `/user/tutors/offline`; `city` is
 * required for OFFLINE discovery (query param) and ignored for ONLINE.
 * Offline `sessionType` defaults to `AT_TEACHER` (space city); `AT_STUDENT`
 * matches tutors' `studentLessonCities`.
 */
export type TutorSearchMode = 'ONLINE' | 'OFFLINE';

/** Offline discovery place filter — maps to API `sessionType` on cities/offline search. */
export type TutorOfflineSessionType = 'AT_TEACHER' | 'AT_STUDENT';

export const TUTOR_SEARCH_MODES: TutorSearchMode[] = ['OFFLINE', 'ONLINE'];

export const TUTOR_OFFLINE_SESSION_TYPES: TutorOfflineSessionType[] = ['AT_TEACHER', 'AT_STUDENT'];

export const DEFAULT_TUTOR_SEARCH_MODE: TutorSearchMode = 'OFFLINE';

export const DEFAULT_OFFLINE_SESSION_TYPE: TutorOfflineSessionType = 'AT_TEACHER';

/** Default city when offline discovery has no explicit city yet. */
export const DEFAULT_OFFLINE_TUTOR_CITY = 'Warszawa';

export function resolveOfflineSearchCity(city: string | null | undefined): string {
  const trimmed = (city ?? '').trim();
  return trimmed || DEFAULT_OFFLINE_TUTOR_CITY;
}

export function isTutorSearchMode(value: unknown): value is TutorSearchMode {
  return value === 'ONLINE' || value === 'OFFLINE';
}

export function isTutorOfflineSessionType(value: unknown): value is TutorOfflineSessionType {
  return value === 'AT_TEACHER' || value === 'AT_STUDENT';
}

/** Coerces an arbitrary value (e.g. query string) into a valid search mode. */
export function resolveTutorSearchMode(value: unknown): TutorSearchMode {
  return isTutorSearchMode(value) ? value : DEFAULT_TUTOR_SEARCH_MODE;
}

/** Coerces query `sessionType` for offline discovery; defaults to at-teacher place. */
export function resolveOfflineSessionType(value: unknown): TutorOfflineSessionType {
  return isTutorOfflineSessionType(value) ? value : DEFAULT_OFFLINE_SESSION_TYPE;
}

/** Offline discovery cannot be submitted without a non-empty city. */
export function isOfflineCityMissing(
  mode: TutorSearchMode,
  city: string | null | undefined
): boolean {
  return mode === 'OFFLINE' && (city ?? '').trim().length === 0;
}

export interface TutorSearchParams {
  mode: TutorSearchMode;
  city: string;
  skills: TutorSkill[];
  /** Only meaningful for OFFLINE; defaults to `AT_TEACHER`. */
  offlineSessionType: TutorOfflineSessionType;
}

function parseTutorSearchSkills(params: URLSearchParams): TutorSkill[] {
  const skillSet = new Set<TutorSkill>();
  for (const value of params.getAll('skill')) {
    const trimmed = value.trim();
    if (TUTOR_SKILLS.includes(trimmed as TutorSkill)) {
      skillSet.add(trimmed as TutorSkill);
    }
  }
  return Array.from(skillSet);
}

function parseTutorSearchFilters(
  search: string
): Pick<TutorSearchParams, 'city' | 'skills' | 'offlineSessionType'> {
  const params = new URLSearchParams(search);
  const city = (params.get('city') ?? params.get('location') ?? '').trim();
  return {
    city,
    skills: parseTutorSearchSkills(params),
    offlineSessionType: resolveOfflineSessionType(params.get('sessionType')),
  };
}

/** Parses legacy `?mode=` query URLs (pre path-based routes). */
export function parseLegacyTutorSearchQuery(search: string): TutorSearchParams {
  const params = new URLSearchParams(search);
  return {
    mode: resolveTutorSearchMode(params.get('mode')),
    ...parseTutorSearchFilters(search),
  };
}

/** Parses pathname + query for `/user/tutors/online` and `/user/tutors/offline`. */
export function parseTutorSearchLocation(pathname: string, search: string): TutorSearchParams {
  const filters = parseTutorSearchFilters(search);
  if (pathname === TUTOR_SEARCH_OFFLINE_PATH) {
    return { mode: 'OFFLINE', ...filters };
  }
  if (pathname === TUTOR_SEARCH_ONLINE_PATH) {
    return { mode: 'ONLINE', ...filters };
  }
  return { mode: DEFAULT_TUTOR_SEARCH_MODE, ...filters };
}

/** @deprecated Use {@link parseTutorSearchLocation} or {@link parseLegacyTutorSearchQuery}. */
export function parseTutorSearchParams(search: string): TutorSearchParams {
  return parseLegacyTutorSearchQuery(search);
}

/**
 * Builds the discovery route path. Offline routes carry `city` (and optional
 * non-default `sessionType`) in the query string; online routes only carry
 * optional `skill` filters.
 */
export function buildTutorSearchPath(input: {
  mode: TutorSearchMode;
  city?: string | null;
  skills?: TutorSkill[] | null;
  offlineSessionType?: TutorOfflineSessionType | null;
  /** @deprecated Use `skills` */
  skill?: TutorSkill | '' | null;
}): string {
  const base = input.mode === 'OFFLINE' ? TUTOR_SEARCH_OFFLINE_PATH : TUTOR_SEARCH_ONLINE_PATH;
  const params = new URLSearchParams();
  if (input.mode === 'OFFLINE') {
    const city = (input.city ?? '').trim();
    if (city) {
      params.set('city', city);
    }
    const offlineSessionType = resolveOfflineSessionType(input.offlineSessionType);
    if (offlineSessionType !== DEFAULT_OFFLINE_SESSION_TYPE) {
      params.set('sessionType', offlineSessionType);
    }
  }
  const skills =
    input.skills && input.skills.length > 0
      ? input.skills
      : input.skill
        ? [input.skill]
        : [];
  for (const skill of skills) {
    params.append('skill', skill);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/** Redirect target for legacy `/user/tutors` and `/user/tutors/results` URLs. */
export function resolveLegacyTutorSearchRedirect(pathname: string, search: string): string {
  if (pathname === `${SEARCH_TUTORS_PATH}/results` || search) {
    const legacy = parseLegacyTutorSearchQuery(search);
    return buildTutorSearchPath({
      mode: legacy.mode,
      city: legacy.mode === 'OFFLINE' ? resolveOfflineSearchCity(legacy.city) : legacy.city,
      skills: legacy.skills,
      offlineSessionType: legacy.offlineSessionType,
    });
  }
  return defaultTutorSearchPath();
}

/** Default discovery landing route (in-person tutors in Warszawa). */
export function defaultTutorSearchPath(): string {
  return buildTutorSearchPath({
    mode: DEFAULT_TUTOR_SEARCH_MODE,
    city: DEFAULT_OFFLINE_TUTOR_CITY,
    offlineSessionType: DEFAULT_OFFLINE_SESSION_TYPE,
  });
}

/** @deprecated Use {@link buildTutorSearchPath}. */
export function buildTutorSearchQuery(input: {
  mode: TutorSearchMode;
  city?: string | null;
  skills?: TutorSkill[] | null;
  offlineSessionType?: TutorOfflineSessionType | null;
  /** @deprecated Use `skills` */
  skill?: TutorSkill | '' | null;
}): string {
  const path = buildTutorSearchPath(input);
  const queryIndex = path.indexOf('?');
  return queryIndex >= 0 ? path.slice(queryIndex + 1) : '';
}
